/**
 * Wave 26.11 — Gallery page.
 * Wave 27.4 — Long-press context menu + Recycle Bin (30-day soft delete).
 *
 * Phone-style grid of every image/video uploaded to HidroVerse, with:
 *  • Filter chips: All / Unassigned / Assigned / Bin (mgr+ only)
 *  • Date-range filter, free-text search.
 *  • Long-press / right-click on any thumb opens an inline action menu:
 *      Delete (→ Bin) · Share · Move to · Assign to.
 *  • Bin items can be Restored or Purged (manager / admin / owner only).
 *  • Auto-purge after 30 days runs as a background sweep in server.py.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search, X, Camera as CameraIcon, CheckSquare, Loader2,
  Image as ImageIcon, Video as VideoIcon, Trash2, Check, ChevronLeft, ChevronRight,
  Tag, Calendar, ExternalLink, Inbox, Sparkles, MoreHorizontal,
  Share2, ArrowRightLeft, RotateCcw, FlameKindling, Trash, AlertTriangle,
  ImageOff, Folder as FolderIcon, Edit3,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import UploadScanBadge from "../components/uploads/UploadScanBadge";
import ShareButton from "../components/ShareButton";
import { shareFile } from "../lib/shareFile";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import DatePickerInput from "../components/DatePickerInput";
import DictTextarea from "../components/DictTextarea";

const getAuthToken = () => {
  try { return localStorage.getItem("hv_token") || ""; }
  catch { return ""; }
};

const STAFF_ENTITY_KINDS = [
  { kind: "customer", label: "Customer" },
  { kind: "jobcard", label: "Job Card" },
  { kind: "ticket", label: "Ticket" },
  { kind: "site", label: "Site" },
];

const KIND_LABEL = {
  customer: "Customer",
  jobcard: "JC",
  ticket: "Ticket",
  site: "Site",
  lead: "Lead",
  meeting: "Meeting",
};

export default function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const visionLabel = `${aiName} Vision`;
  const isManager = !!user && ["owner", "admin", "manager"].includes(user.role);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 60;
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [assigned, setAssigned] = useState(searchParams.get("assigned") || "all");
  const [kind, setKind] = useState(searchParams.get("kind") || "all");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");
  const [inBin, setInBin] = useState(searchParams.get("bin") === "1");
  // [Phase 2 + 3] Tree-view folder nav + sort control.
  const [folderPath, setFolderPath] = useState(() => {
    const k = searchParams.get("fk"); const i = searchParams.get("fi");
    return k && i ? { kind: k, id: i, name: searchParams.get("fn") || "" } : null;
  });
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "newest");
  const [tree, setTree] = useState(null);
  const [renameOpen, setRenameOpen] = useState(null); // item to rename
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [lightbox, setLightbox] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(null); // item to move
  const [ctxMenu, setCtxMenu] = useState(null); // { item, x, y }
  // Wave 29 Chunk 10j — infinite-scroll sentinel.
  const sentinelRef = useRef(null);

  // Build the query for a single page. When the user changes filters we
  // reset `skip` (and the items array) so the page reads top-down again.
  const fetchPage = useCallback(async (skipVal, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { limit, skip: skipVal };
      if (q) params.q = q;
      if (assigned !== "all" && !inBin) params.assigned = assigned;
      if (kind !== "all") params.kind = kind;
      if (fromDate) params.from_date = fromDate;
      if (toDate) {
        const t = new Date(toDate);
        t.setHours(23, 59, 59, 999);
        params.to_date = t.toISOString();
      }
      if (inBin) params.bin = 1;
      // [Phase 3] Sort passthrough.
      if (sortBy && sortBy !== "newest") params.sort = sortBy;
      // [Phase 2] Folder-drilldown scoping.
      if (folderPath?.kind && folderPath?.id) {
        params.folder_kind = folderPath.kind;
        params.folder_id = folderPath.id;
      }
      const { data } = await api.get("/gallery", { params });
      setItems((prev) => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load gallery.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [q, assigned, kind, inBin, fromDate, toDate, sortBy, folderPath]);

  // [Phase 2] Fetch the tree once on mount + whenever we leave bin mode.
  useEffect(() => {
    if (inBin) return;
    (async () => {
      try {
        const { data } = await api.get("/gallery/tree");
        setTree(data);
      } catch (e) {
        // Non-fatal — tree is a nav aid, not required for grid render.
      }
    })();
  }, [inBin]);

  // Reset & refetch top whenever a filter changes.
  useEffect(() => {
    setItems([]);
    setSkip(0);
    fetchPage(0, false);
  }, [fetchPage]);

  // Infinite-scroll: when sentinel enters the viewport AND there's more,
  // bump skip and fetch the next page (appending).
  useEffect(() => {
    if (loading || loadingMore) return;
    const target = sentinelRef.current;
    if (!target) return;
    if (items.length >= total) return; // all loaded
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e?.isIntersecting) {
        const next = items.length;
        setSkip(next);
        fetchPage(next, true);
      }
    }, { rootMargin: "400px" });
    io.observe(target);
    return () => io.disconnect();
  }, [items.length, total, loading, loadingMore, fetchPage]);

  // Expose a manual `load()` that re-fetches the current view from top
  // (used by delete / restore / bulk handlers below).
  const load = useCallback(() => {
    setItems([]);
    setSkip(0);
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (assigned !== "all") next.assigned = assigned;
    if (kind !== "all") next.kind = kind;
    if (fromDate) next.from = fromDate;
    if (toDate) next.to = toDate;
    if (inBin) next.bin = "1";
    setSearchParams(next, { replace: true });
  }, [q, assigned, kind, fromDate, toDate, inBin, setSearchParams]);

  // Close the context menu on Esc / outside click / scroll.
  useEffect(() => {
    if (!ctxMenu) return;
    const handler = () => setCtxMenu(null);
    const esc = (e) => { if (e.key === "Escape") setCtxMenu(null); };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
      window.removeEventListener("keydown", esc);
    };
  }, [ctxMenu]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => { setSelected(new Set()); setSelectMode(false); };

  // ── Single-item actions ─────────────────────────────────────────
  const handleDelete = async (item) => {
    try {
      await api.delete(`/gallery/${item.id}`);
      toast.success("Moved to Bin (30-day retention).");
      setCtxMenu(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move to bin.");
    }
  };

  const handleRestore = async (item) => {
    try {
      await api.post(`/gallery/${item.id}/bin-restore`);
      toast.success("Restored from Bin.");
      setCtxMenu(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    }
  };

  const handlePurge = async (item) => {
    if (!confirm("Permanently delete this file? This cannot be undone.")) return;
    try {
      await api.delete(`/gallery/${item.id}/bin-purge`);
      toast.success("Permanently deleted.");
      setCtxMenu(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed.");
    }
  };

  // ── Bulk bin actions ────────────────────────────────────────────
  const bulkBinAction = async (path, verb) => {
    if (selected.size === 0) return;
    try {
      const { data } = await api.post(`/gallery/${path}-bulk`, {
        upload_ids: [...selected],
      });
      toast.success(`${data[verb] || selected.size} item${selected.size === 1 ? "" : "s"} ${verb === "restored" ? "restored" : "permanently deleted"}.`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await Promise.all([...selected].map((id) => api.delete(`/gallery/${id}`)));
      toast.success(`${selected.size} item${selected.size === 1 ? "" : "s"} moved to Bin.`);
      clearSelection();
      load();
    } catch (e) {
      toast.error("Some items could not be moved to Bin.");
      load();
    }
  };

  // ── Long-press / context-menu handler ──────────────────────────
  const openCtxMenu = (item, evt) => {
    let x = 0, y = 0;
    if (evt?.clientX != null) { x = evt.clientX; y = evt.clientY; }
    else if (evt?.touches?.[0]) { x = evt.touches[0].clientX; y = evt.touches[0].clientY; }
    // Keep menu inside viewport.
    const w = window.innerWidth, h = window.innerHeight;
    x = Math.max(12, Math.min(w - 220, x));
    y = Math.max(12, Math.min(h - 280, y));
    setCtxMenu({ item, x, y });
  };

  return (
    <div className="space-y-4" data-testid="gallery-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            {inBin ? <><Trash className="w-6 h-6 text-amber-400" /> Bin</> : "Gallery"}
          </h1>
          <p className="text-xs text-[#647087] mt-0.5">
            {total.toLocaleString()} item{total === 1 ? "" : "s"} ·
            {" "}{inBin
              ? "Items here auto-delete 30 days after moving to Bin."
              : (selectMode ? `${selected.size} selected` : "Long-press (or right-click) any photo for actions")}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!inBin && (
            <Link
              to="/camera"
              className="hv-btn hv-btn-primary text-xs"
              data-testid="gallery-camera-btn"
              title="Open full-page Camera"
            >
              <CameraIcon className="w-3.5 h-3.5" /> Camera
            </Link>
          )}
          {!selectMode ? (
            <button
              type="button"
              onClick={() => setSelectMode(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="gallery-select-mode"
              title="Select multiple"
            >
              <CheckSquare className="w-3.5 h-3.5" /> Select
            </button>
          ) : (
            <>
              {/* 2026-02-18 Bug-bash — Select all/none toggle so users don't
                  have to tap every thumb when binning a whole day of photos. */}
              <button
                type="button"
                onClick={() => {
                  const visibleIds = items.map((it) => it.id);
                  const allChosen = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
                  if (allChosen) {
                    setSelected(new Set());
                  } else {
                    setSelected(new Set(visibleIds));
                  }
                }}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="gallery-select-all"
                title="Select all visible / clear"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                {items.length > 0 && items.every((it) => selected.has(it.id)) ? "Clear" : "All"}
              </button>
              {inBin ? (
                <>
                  <button
                    type="button"
                    onClick={() => bulkBinAction("restore", "restored")}
                    disabled={selected.size === 0}
                    className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                    data-testid="gallery-bulk-restore"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore ({selected.size})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!confirm(`Permanently delete ${selected.size} item${selected.size === 1 ? "" : "s"}?`)) return;
                      bulkBinAction("purge", "purged");
                    }}
                    disabled={selected.size === 0}
                    className="hv-btn text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                    data-testid="gallery-bulk-purge"
                  >
                    <Trash className="w-3.5 h-3.5" /> Delete forever
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setAssignOpen(true)}
                    disabled={selected.size === 0}
                    className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
                    data-testid="gallery-bulk-assign"
                  >
                    <Tag className="w-3.5 h-3.5" /> Assign ({selected.size})
                  </button>
                  <button
                    type="button"
                    onClick={bulkDelete}
                    disabled={selected.size === 0}
                    className="hv-btn text-xs border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 disabled:opacity-50"
                    data-testid="gallery-bulk-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Bin ({selected.size})
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={clearSelection}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="gallery-clear-selection"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <FilterChip
          label={<><ImageIcon className="w-3 h-3" /> Gallery</>}
          active={!inBin}
          onClick={() => { setInBin(false); clearSelection(); }}
          testId="gallery-filter-not-bin"
        />
        {isManager && (
          <FilterChip
            label={<><Trash className="w-3 h-3" /> Bin</>}
            active={inBin}
            onClick={() => { setInBin(true); clearSelection(); }}
            testId="gallery-filter-bin"
            danger
          />
        )}
        {!inBin && (
          <>
            <span className="text-[#647087] text-xs mx-1">·</span>
            <FilterChip label="All" active={assigned === "all"} onClick={() => setAssigned("all")} testId="gallery-filter-all" />
            <FilterChip label="Unassigned" active={assigned === "unassigned"} onClick={() => setAssigned("unassigned")} testId="gallery-filter-unassigned" />
            <FilterChip label="Assigned" active={assigned === "assigned"} onClick={() => setAssigned("assigned")} testId="gallery-filter-assigned" />
            <span className="text-[#647087] text-xs mx-1">·</span>
            <FilterChip label="All media" active={kind === "all"} onClick={() => setKind("all")} testId="gallery-filter-kind-all" />
            <FilterChip label={<><ImageIcon className="w-3 h-3" /> Photos</>} active={kind === "image"} onClick={() => setKind("image")} testId="gallery-filter-image" />
            <FilterChip label={<><VideoIcon className="w-3 h-3" /> Videos</>} active={kind === "video"} onClick={() => setKind("video")} testId="gallery-filter-video" />
          </>
        )}
      </div>

      {/* Search + Date-range + Sort */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try “leaking pump”, filename, tag…"
            className="hv-input text-xs pl-8 pr-7 w-full"
            data-testid="gallery-search-input"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087]" aria-label="Clear">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {/* [Phase 3] Sort dropdown */}
        <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
          <span>Sort</span>
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setSkip(0); }}
            className="hv-input text-[11px] py-1 px-2"
            data-testid="gallery-sort-select"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A→Z</option>
            <option value="size_desc">Largest first</option>
            <option value="size_asc">Smallest first</option>
            <option value="type">By type</option>
          </select>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-[#a0aabf]">
          <Calendar className="w-3.5 h-3.5 text-[#647087]" aria-hidden="true" />
          <div className="w-[140px]">
            <DatePickerInput
              value={fromDate}
              onChange={(v) => { setFromDate(v); setSkip(0); }}
              testId="gallery-from-date"
              placeholder="From"
            />
          </div>
          <span className="text-[#647087]">→</span>
          <div className="w-[140px]">
            <DatePickerInput
              value={toDate}
              onChange={(v) => { setToDate(v); setSkip(0); }}
              testId="gallery-to-date"
              placeholder="To"
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => { setFromDate(""); setToDate(""); setSkip(0); }}
              className="text-[#647087] hover:text-white"
              data-testid="gallery-clear-dates"
              aria-label="Clear dates"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square bg-white/[0.04] rounded animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="hv-card p-10 text-center text-[#647087] text-sm" data-testid="gallery-empty">
          <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {inBin ? (
            <div>Bin is empty.</div>
          ) : q ? (
            <>
              <div>No matches for “{q}”.</div>
              <div className="text-xs mt-1">{visionLabel} describes new photos automatically — try a different phrase, or check back in a minute if you just uploaded.</div>
            </>
          ) : (fromDate || toDate) ? (
            <>
              <div>No photos in that date range.</div>
              <div className="text-xs mt-1">Clear the dates to see everything.</div>
            </>
          ) : (
            <>
              <div>No items match these filters.</div>
              <div className="text-xs mt-1">Tap Camera to add photos.</div>
            </>
          )}
        </div>
      ) : (
        <>
          {/* [Phase 2] Folder tree — breadcrumb + folder tiles.
              Only rendered when NOT in bin and NOT drilled into a folder. */}
          {!inBin && !folderPath && tree?.roots && !q && (
            <div className="space-y-3" data-testid="gallery-tree">
              <div className="text-[11px] uppercase tracking-wide text-[#647087] font-medium">
                Browse by folder
              </div>
              {tree.roots.map((root) => (
                root.folders.length > 0 && (
                  <div key={root.kind} className="space-y-1.5">
                    <div className="text-[11px] text-[#a0aabf] flex items-center gap-2">
                      <span className="font-medium">{root.label}</span>
                      <span className="text-[#647087]">· {root.folders.length}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {root.folders.slice(0, 24).map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => {
                            setFolderPath({ kind: root.kind, id: f.id, name: f.name });
                            setSkip(0);
                          }}
                          className="group rounded-md border border-[#1b2432] bg-[#0f1724] hover:border-[#1FB6C1]/60 hover:bg-[#111c2d] p-2.5 text-left transition"
                          data-testid={`gallery-folder-${root.kind}-${f.id}`}
                          title={f.name}
                        >
                          <div className="flex items-center gap-1.5 mb-1 text-[#1FB6C1]">
                            <FolderIcon className="w-3.5 h-3.5" />
                            <span className="text-[10px] uppercase tracking-wide text-[#647087]">{root.kind}</span>
                            <span className="ml-auto text-[10px] text-[#647087]">{f.count}</span>
                          </div>
                          <div className="text-xs text-white line-clamp-2 leading-tight">{f.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              ))}
              <div className="pt-2 text-[11px] uppercase tracking-wide text-[#647087] font-medium">
                All media
              </div>
            </div>
          )}
          {/* [Phase 2] Breadcrumb for drilled-in folder */}
          {folderPath && (
            <div className="flex items-center gap-2 text-xs text-[#a0aabf] mb-2" data-testid="gallery-breadcrumb">
              <button
                type="button"
                onClick={() => { setFolderPath(null); setSkip(0); }}
                className="hover:text-white underline"
                data-testid="gallery-breadcrumb-root"
              >
                Gallery
              </button>
              <span className="text-[#647087]">›</span>
              <span className="text-white">{folderPath.name || folderPath.kind}</span>
              <button
                type="button"
                onClick={() => { setFolderPath(null); setSkip(0); }}
                className="ml-2 text-[#647087] hover:text-white"
                aria-label="Exit folder"
                data-testid="gallery-breadcrumb-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5" data-testid="gallery-grid">
          {items.map((it) => (
            <Thumb
              key={it.id}
              item={it}
              selected={selected.has(it.id)}
              selectMode={selectMode}
              inBin={inBin}
              onToggle={() => toggleSelect(it.id)}
              onOpen={() => setLightbox(it)}
              onLongPress={(evt) => openCtxMenu(it, evt)}
              onEnterSelectMode={(id) => {
                setSelectMode(true);
                setSelected((prev) => {
                  const next = new Set(prev);
                  next.add(id);
                  return next;
                });
              }}
            />
          ))}
        </div>
        </>
      )}

      {/* Infinite-scroll sentinel + "Loading more" hint */}
      {!loading && (
        <div ref={sentinelRef} data-testid="gallery-sentinel" className="h-12 flex items-center justify-center text-xs text-[#647087]">
          {loadingMore ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading more…
            </span>
          ) : items.length >= total && total > 0 ? (
            <span>All {total.toLocaleString()} items loaded.</span>
          ) : null}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          item={lightbox}
          items={items}
          onNavigate={(it) => setLightbox(it)}
          inBin={inBin}
          onClose={() => setLightbox(null)}
          onChange={load}
        />
      )}

      {/* Bulk assign drawer */}
      {assignOpen && (
        <AssignDrawer
          uploadIds={[...selected]}
          onClose={() => setAssignOpen(false)}
          onDone={(n) => {
            toast.success(`${n} item${n === 1 ? "" : "s"} assigned.`);
            setAssignOpen(false);
            clearSelection();
            load();
          }}
        />
      )}

      {/* Single-item Move dialog */}
      {moveOpen && (
        <MoveDialog
          item={moveOpen}
          onClose={() => setMoveOpen(null)}
          onDone={() => {
            toast.success("Moved.");
            setMoveOpen(null);
            load();
          }}
        />
      )}

      {/* [Phase 4] Rename dialog */}
      {renameOpen && (
        <RenameDialog
          item={renameOpen}
          onClose={() => setRenameOpen(null)}
          onDone={() => {
            toast.success("Renamed.");
            setRenameOpen(null);
            load();
          }}
        />
      )}

      {/* Context menu (long-press / right-click) — single-item or bulk depending on selection state */}
      {ctxMenu && (
        <ContextMenu
          item={ctxMenu.item}
          x={ctxMenu.x}
          y={ctxMenu.y}
          inBin={inBin}
          isManager={isManager}
          bulkCount={selectMode && selected.has(ctxMenu.item.id) ? selected.size : 0}
          onClose={() => setCtxMenu(null)}
          onDelete={() => handleDelete(ctxMenu.item)}
          onRestore={() => handleRestore(ctxMenu.item)}
          onPurge={() => handlePurge(ctxMenu.item)}
          onAssign={() => {
            const ids = (selectMode && selected.has(ctxMenu.item.id) && selected.size > 0)
              ? selected
              : new Set([ctxMenu.item.id]);
            setSelected(ids);
            setAssignOpen(true);
            setCtxMenu(null);
          }}
          onMove={() => {
            setMoveOpen(ctxMenu.item);
            setCtxMenu(null);
          }}
          onRename={() => {
            setRenameOpen(ctxMenu.item);
            setCtxMenu(null);
          }}
          onBulkDelete={() => { setCtxMenu(null); bulkDelete(); }}
          onBulkRestore={() => { setCtxMenu(null); bulkBinAction("restore", "restored"); }}
          onBulkPurge={() => {
            if (window.confirm(`Permanently delete ${selected.size} item${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) {
              setCtxMenu(null);
              bulkBinAction("purge", "purged");
            }
          }}
        />
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, testId, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 border transition-colors ${
        active
          ? danger
            ? "bg-amber-400 text-[#0a0b0e] border-amber-400"
            : "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]"
          : "border-white/15 text-[#a0aabf] hover:bg-white/5"
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

// [Iter 335 — 2026-02-25] Clean placeholder for tiles whose thumbnail
// bytes are missing from R2 / Emergent (legacy row, purged bucket, or
// bytes-not-migrated). Replaces the previous "dimmed grey square" that
// looked identical to a native browser broken-image icon and made the
// gallery feel broken.
function MissingPhotoTile() {
  return (
    <div
      data-testid="gallery-thumb-missing"
      className="w-full h-full flex flex-col items-center justify-center gap-1 bg-[#12151b] text-[#647087] pointer-events-none"
      title="Original photo file is missing"
    >
      <ImageOff className="w-5 h-5 opacity-70" />
      <span className="text-[9px] uppercase tracking-wider font-medium">photo missing</span>
    </div>
  );
}

function Thumb({ item, selected, selectMode, inBin, onToggle, onOpen, onLongPress, onEnterSelectMode }) {
  const isVideo = item.kind === "video";
  const longPressTimer = useRef(null);
  const longPressed = useRef(false);
  // [Iter 335 — 2026-02-25] Track load failure so we can render a
  // clean "photo missing" placeholder INSTEAD of a native browser
  // broken-image icon (which looks like a bug) or a dim empty tile.
  // Once we know a tile is broken we STOP fetching it (source blanks
  // to a data:image no-op) so the browser doesn't retry endlessly
  // and eat the parallel-connection budget for the rest of the grid.
  const [imgBroken, setImgBroken] = useState(false);
  // 2026-02-18 Bug-bash — video poster fallback. Some Android browsers ignore
  // the `#t=0.5` media fragment and render a black thumbnail. We grab the
  // first frame onto a hidden canvas as a data-URL fallback after metadata
  // loads. Falls back gracefully to the bare `<video>` if canvas is blocked
  // (CORS / cross-origin streams).
  const [videoPoster, setVideoPoster] = useState(null);
  const videoRef = useRef(null);

  const captureFrame = useCallback(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(320, v.videoWidth);
      canvas.height = Math.round(canvas.width * (v.videoHeight / v.videoWidth));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      setVideoPoster(canvas.toDataURL("image/jpeg", 0.7));
    } catch {
      // Cross-origin frame — leave the bare <video> in place.
    }
  }, [isVideo]);

  const startLP = (evt) => {
    longPressed.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressed.current = true;
      // 2026-02-18 Bug-bash gallery improvements — if we're NOT in select
      // mode yet, long-press now PROMOTES into select mode AND selects this
      // item (standard mobile photo gallery UX). Keep the context menu as
      // an alternative path via right-click + the Select header button.
      if (!selectMode && typeof onEnterSelectMode === "function") {
        try {
          if ("vibrate" in navigator) navigator.vibrate(20);
        } catch { /* noop */ }
        onEnterSelectMode(item.id);
      } else {
        onLongPress(evt);
      }
    }, 500);
  };
  const cancelLP = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleClick = () => {
    if (longPressed.current) {
      longPressed.current = false;
      return;
    }
    if (selectMode) onToggle();
    else onOpen();
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    onLongPress(e);
  };

  // Primary assignment chip — first non-customer if available, else customer.
  const primaryAssignment = useMemo(() => {
    const arr = item.assignments || [];
    if (arr.length === 0) return null;
    const nonCust = arr.find((a) => a.kind !== "customer");
    return nonCust || arr[0];
  }, [item.assignments]);

  return (
    <button
      type="button"
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onTouchStart={(e) => startLP(e)}
      onTouchEnd={cancelLP}
      onTouchCancel={cancelLP}
      onTouchMove={cancelLP}
      onMouseDown={(e) => { if (e.button === 0) startLP(e); }}
      onMouseUp={cancelLP}
      onMouseLeave={cancelLP}
      className={`relative aspect-square rounded overflow-hidden bg-black/40 border transition-colors select-none ${
        selected ? "border-[#1FB6C1] ring-2 ring-[#1FB6C1]" : "border-white/5 hover:border-[#1FB6C1]/40"
      }`}
      data-testid={`gallery-thumb-${item.id}`}
    >
      {isVideo ? (
        videoPoster ? (
          <img
            src={videoPoster}
            alt={item.original_filename}
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <video
            ref={videoRef}
            src={`${item.url}?auth=${encodeURIComponent(getAuthToken())}#t=0.5`}
            preload="metadata"
            muted
            playsInline
            crossOrigin="anonymous"
            onLoadedData={captureFrame}
            onSeeked={captureFrame}
            className="w-full h-full object-cover pointer-events-none bg-[#1a1d24]"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
          />
        )
      ) : imgBroken ? (
        <MissingPhotoTile />
      ) : (
        <img
          src={`${item.thumbnail_url}?auth=${encodeURIComponent(getAuthToken())}`}
          alt={item.original_filename}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover pointer-events-none"
          onError={() => setImgBroken(true)}
        />
      )}
      {/* Video play indicator overlay */}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center">
            <VideoIcon className="w-3.5 h-3.5 text-white" />
          </div>
        </div>
      )}
      {/* Top-left badges */}
      <div className="absolute top-1 left-1 flex flex-col gap-1 pointer-events-none">
        {inBin ? (
          <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-400/90 text-[#0a0b0e] font-bold">BIN</span>
        ) : item.is_unassigned ? (
          <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-500/80 text-[#0a0b0e] font-bold">UN</span>
        ) : null}
        {isVideo && (
          <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-black/70 text-white inline-flex items-center gap-0.5">
            <VideoIcon className="w-2.5 h-2.5" />
          </span>
        )}
      </div>
      {/* Top-right: Hidro Vision scan status (compact pulse) — Dirk 2026-02 */}
      {!isVideo && (
        <div className="absolute top-1 right-1 pointer-events-auto z-10" onClick={(e) => e.stopPropagation()}>
          <UploadScanBadge
            uploadId={item.id}
            initialStatus={item.vision_describe_status}
            compact
          />
        </div>
      )}
      {/* Bottom assignment chip */}
      {!inBin && primaryAssignment && (
        <div className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-gradient-to-t from-black/85 via-black/60 to-transparent text-[9px] leading-tight text-white truncate text-left pointer-events-none">
          <span className="opacity-80">{KIND_LABEL[primaryAssignment.kind] || primaryAssignment.kind}</span>
          <span className="opacity-100"> · {primaryAssignment.label}</span>
        </div>
      )}
      {selectMode && (
        <div className={`absolute top-1 right-1 w-5 h-5 rounded-full border-2 flex items-center justify-center pointer-events-none ${
          selected ? "bg-[#1FB6C1] border-[#1FB6C1]" : "border-white/70 bg-black/30"
        }`}>
          {selected && <Check className="w-3 h-3 text-[#0a0b0e]" />}
        </div>
      )}
    </button>
  );
}

function ContextMenu({ item, x, y, inBin, isManager, bulkCount = 0, onClose, onDelete, onRestore, onPurge, onAssign, onMove, onRename, onBulkDelete, onBulkRestore, onBulkPurge }) {
  const token = getAuthToken();
  const fileUrl = `${item.url}?auth=${encodeURIComponent(token)}`;
  const isBulk = bulkCount > 1;
  return (
    <>
      <div
        className="fixed inset-0 z-[210]"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        data-testid="gallery-ctx-backdrop"
      />
      <div
        className="fixed z-[211] w-[230px] rounded-lg border border-white/10 bg-[#0e1015] shadow-2xl shadow-black/60 py-1 text-xs animate-in"
        style={{ left: x, top: y }}
        data-testid="gallery-ctx-menu"
      >
        {isBulk && (
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#1FB6C1] border-b border-white/5">
            {bulkCount} selected
          </div>
        )}
        {inBin ? (
          <>
            {isBulk ? (
              <>
                <CtxItem icon={RotateCcw} label={`Restore ${bulkCount}`} onClick={onBulkRestore} testId="ctx-bulk-restore" />
                {isManager && (
                  <CtxItem icon={Trash} label={`Delete ${bulkCount} forever`} onClick={onBulkPurge} testId="ctx-bulk-purge" danger />
                )}
              </>
            ) : (
              <>
                <CtxItem icon={RotateCcw} label="Restore" onClick={onRestore} testId="ctx-restore" />
                {isManager && (
                  <CtxItem icon={Trash} label="Delete forever" onClick={onPurge} testId="ctx-purge" danger />
                )}
              </>
            )}
          </>
        ) : (
          <>
            <CtxItem
              icon={Tag}
              label={isBulk ? `Assign ${bulkCount}…` : "Assign to…"}
              onClick={onAssign}
              testId={isBulk ? "ctx-bulk-assign" : "ctx-assign"}
            />
            {!isBulk && (
              <CtxItem icon={ArrowRightLeft} label="Move to…" onClick={onMove} testId="ctx-move" />
            )}
            {!isBulk && (
              <CtxItem icon={Edit3} label="Rename…" onClick={onRename} testId="ctx-rename" />
            )}
            {!isBulk && (
              <button
                type="button"
                onClick={async () => {
                  onClose();
                  await shareFile({
                    url: fileUrl,
                    filename: item.original_filename,
                    contentType: item.content_type,
                    title: item.original_filename || "media",
                  });
                }}
                className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-white/[0.06] text-[#d7dde8]"
                data-testid="ctx-share-file"
              >
                <Share2 className="w-3.5 h-3.5 text-[#1FB6C1]" />
                <span>Share file…</span>
              </button>
            )}
            {!isBulk && (
              <a
                href={fileUrl}
                download={item.original_filename}
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-2 hover:bg-white/[0.06] text-[#d7dde8]"
                data-testid="ctx-download"
              >
                <Share2 className="w-3.5 h-3.5" /> Download
              </a>
            )}
            <div className="h-px bg-white/5 my-1" />
            {isBulk ? (
              <CtxItem icon={Trash2} label={`Move ${bulkCount} to Bin`} onClick={onBulkDelete} testId="ctx-bulk-delete" danger />
            ) : (
              <CtxItem icon={Trash2} label="Move to Bin" onClick={onDelete} testId="ctx-delete" danger />
            )}
          </>
        )}
      </div>
    </>
  );
}

function CtxItem({ icon: Icon, label, onClick, testId, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-white/[0.06] ${
        danger ? "text-red-300 hover:text-red-200" : "text-[#d7dde8]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}

function Lightbox({ item, items, onNavigate, inBin, onClose, onChange }) {
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const visionLabel = `${aiName} Vision`;
  const isVideo = item.kind === "video";
  const token = getAuthToken();
  // Wave 29 Chunk 10j — lightbox renders the 1280px display variant (when
  // available); falls back to the original. ShareButton + downloads use
  // the raw URL so customers receive full resolution.
  const baseUrl = isVideo ? item.url : (item.display_url || item.url);
  const src = `${baseUrl}?auth=${encodeURIComponent(token)}`;
  const downloadUrl = `${item.url}?auth=${encodeURIComponent(token)}`;
  const [describing, setDescribing] = useState(false);
  const [desc, setDesc] = useState(item.description || "");
  const [tags, setTags] = useState(item.tags || []);
  const [descOk, setDescOk] = useState(item.vision_describe_ok);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  // 2026-02-18 Bug-bash — track in-flight drag so the image translates with
  // the finger (visual feedback) before deciding whether to commit a swipe.
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Wave 29 Chunk 8c — phone-style prev/next + swipe (Dirk bug 2026-05-11).
  // We accept the full `items` array so we can navigate without re-mounting
  // the Lightbox on every move.
  const list = Array.isArray(items) ? items : [item];
  const idx = list.findIndex((x) => x.id === item.id);
  const hasNav = list.length > 1 && idx >= 0;
  const goPrev = () => { if (!hasNav) return; const i = (idx - 1 + list.length) % list.length; onNavigate && onNavigate(list[i]); };
  const goNext = () => { if (!hasNav) return; const i = (idx + 1) % list.length; onNavigate && onNavigate(list[i]); };

  // Sync local Vision state with the focused item.
  useEffect(() => {
    setDesc(item.description || "");
    setTags(item.tags || []);
    setDescOk(item.vision_describe_ok);
  }, [item]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, hasNav]);

  const runDescribe = async () => {
    setDescribing(true);
    try {
      const { data } = await api.post(`/gallery/${item.id}/describe`);
      if (data?.ok) {
        setDesc(data.description || "");
        setTags(data.tags || []);
        setDescOk(true);
        toast.success(`${aiName} described this photo.`);
        onChange && onChange();
      } else if (data?.reason === "not_image") {
        toast.info("Vision works on photos only.");
      } else {
        toast.error(`${visionLabel} is temporarily unavailable.`);
      }
    } catch (e) {
      toast.error(`Couldn't run ${visionLabel}.`);
    } finally { setDescribing(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur flex flex-col"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="lightbox-close">
          <X className="w-3.5 h-3.5" /> Close
        </button>
        <span className="text-xs text-[#a0aabf] truncate max-w-[40%] flex items-center gap-2">
          {hasNav && (
            <span className="text-[10px] tabular-nums text-[#647087]" data-testid="lightbox-counter">{idx + 1}/{list.length}</span>
          )}
          <span className="truncate">{item.original_filename}</span>
        </span>
        <div className="flex items-center gap-2">
          <ShareButton
            kind="upload"
            refId={item.id}
            title={item.original_filename}
            downloadUrl={downloadUrl}
            compact
            testId="lightbox-share"
          />
          <span className="text-[10px] text-[#647087]">{new Date(item.created_at).toLocaleString()}</span>
        </div>
      </div>
      <div
        className="flex-1 flex items-center justify-center p-2 min-h-0 relative select-none"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
          touchStartY.current = e.touches[0].clientY;
        }}
        onTouchMove={(e) => {
          if (touchStartX.current == null) return;
          const dx = e.touches[0].clientX - touchStartX.current;
          const dy = e.touches[0].clientY - touchStartY.current;
          // Decide axis after 8px of intent so we don't shimmer.
          if (Math.abs(dx) > Math.abs(dy)) {
            setDragOffset({ x: dx, y: 0 });
          } else if (dy > 0) {
            // Only allow downward drag-to-dismiss.
            setDragOffset({ x: 0, y: dy });
          }
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          const dy = e.changedTouches[0].clientY - touchStartY.current;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 60) {
            (dx > 0 ? goPrev() : goNext());
          } else if (dy > 110) {
            // Swipe-down-to-close — matches iOS Photos / Google Photos.
            onClose?.();
          }
          setDragOffset({ x: 0, y: 0 });
          touchStartX.current = null;
          touchStartY.current = null;
        }}
      >
        {hasNav && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-white z-10"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        {isVideo ? (
          <video
            src={src}
            controls
            className="max-w-full max-h-full transition-transform"
            style={dragOffset.x || dragOffset.y ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` } : undefined}
          />
        ) : (
          <img
            src={src}
            alt={item.original_filename}
            className="max-w-full max-h-full object-contain transition-transform"
            style={dragOffset.x || dragOffset.y ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`, opacity: dragOffset.y > 0 ? Math.max(0.3, 1 - dragOffset.y / 400) : 1 } : undefined}
            draggable={false}
          />
        )}
        {hasNav && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-white z-10"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
      {/* Vision description + tags */}
      {!isVideo && (
        <div className="px-3 py-2 border-t border-white/10 bg-[#0a0b0e]" onClick={(e) => e.stopPropagation()}>
          {desc ? (
            <div className="text-[12px] text-[#d7dde8] leading-snug">
              <Sparkles className="w-3 h-3 inline mr-1 text-[#1FB6C1]" />
              <span>{desc}</span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-[#647087] italic">
                {descOk === false ? `${visionLabel} skipped this image.` : `${aiName} hasn't described this image yet.`}
              </span>
              <button
                type="button"
                onClick={runDescribe}
                disabled={describing}
                className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50"
                data-testid="lightbox-describe-btn"
              >
                {describing
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> Describing…</>
                  : <><Sparkles className="w-3 h-3 text-[#1FB6C1]" /> Describe</>
                }
              </button>
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {tags.slice(0, 12).map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#1FB6C1]/25 text-[#a0aabf] bg-[#1FB6C1]/[0.05]">{t}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Footer: assignments + bin meta */}
      <div className="px-3 py-2 border-t border-white/10 bg-[#0a0b0e]" onClick={(e) => e.stopPropagation()}>
        {inBin ? (
          <div className="text-[11px] text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" />
            In Bin — will be permanently deleted on{" "}
            {item.bin_purge_at
              ? new Date(item.bin_purge_at).toLocaleDateString("en-ZA")
              : "soon"}
            {item.deleted_by_name ? ` (sent by ${item.deleted_by_name})` : ""}
          </div>
        ) : (item.assignments || []).length === 0 ? (
          <div className="text-[11px] text-amber-300">⚠ Unassigned — long-press the thumb to attach it.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {item.assignments.map((a) => (
              <Link
                key={`${a.kind}-${a.id}`}
                to={a.url}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-[#1FB6C1]/40 text-[#1FB6C1] hover:bg-[#1FB6C1]/10"
                data-testid={`lightbox-assignment-${a.kind}-${a.id}`}
              >
                {KIND_LABEL[a.kind] || a.kind} · {a.label}
                <ExternalLink className="w-2.5 h-2.5" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RenameDialog({ item, onClose, onDone }) {
  const [displayName, setDisplayName] = useState(item.display_name || item.original_filename || "");
  const [description, setDescription] = useState(item.description || "");
  const [tagsText, setTagsText] = useState(Array.isArray(item.tags) ? item.tags.join(", ") : "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/gallery/${item.id}`, {
        display_name: displayName.trim() || null,
        description: description.trim() || null,
        tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0e1015] p-5" onClick={(e) => e.stopPropagation()} data-testid="gallery-rename-dialog">
        <div className="text-sm font-medium text-white mb-3">Rename / describe</div>
        <label className="block text-[11px] text-[#a0aabf] mb-1">Display name</label>
        <input
          className="hv-input text-xs w-full mb-3"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="How this item appears in the gallery"
          data-testid="gallery-rename-display-name"
          maxLength={200}
        />
        <label className="block text-[11px] text-[#a0aabf] mb-1">Description (overrides AI caption)</label>
        <DictTextarea
          className="hv-input text-xs w-full mb-3 h-20"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What's in this image / video"
          data-testid="gallery-rename-description"
          maxLength={2000}
        />
        <label className="block text-[11px] text-[#a0aabf] mb-1">Tags (comma-separated)</label>
        <input
          className="hv-input text-xs w-full mb-4"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          placeholder="pump, leak, bearing"
          data-testid="gallery-rename-tags"
        />
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="gallery-rename-cancel">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs" data-testid="gallery-rename-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}


function MoveDialog({ item, onClose, onDone }) {
  const [kind, setKind] = useState("customer");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let endpoint = "/customers";
        let params = { q: query, limit: 12 };
        if (kind === "jobcard") { endpoint = "/jobcards"; params = { search: query, limit: 12 }; }
        else if (kind === "ticket") { endpoint = "/tickets"; params = { q: query, limit: 12 }; }
        else if (kind === "site") { endpoint = "/sites"; params = { q: query, limit: 12 }; }
        const { data } = await api.get(endpoint, { params, signal: ctl.signal });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setResults(arr.slice(0, 12));
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, kind]);

  const labelFor = (it) => {
    if (kind === "customer") return it.name || it.trading_name || it.id;
    if (kind === "jobcard") return `${it.short_id || it.ref || it.id?.slice(0, 8)} — ${it.title || ""}`.trim();
    if (kind === "ticket") return `${it.short_id || it.ref || it.id?.slice(0, 8)} — ${it.subject || it.title || ""}`.trim();
    if (kind === "site") return it.label || it.name || it.id;
    return it.id;
  };

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      await api.post(`/gallery/${item.id}/move`, { kind, id: picked.id });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur flex flex-col" onClick={onClose} data-testid="gallery-move-dialog">
      <div
        className="bg-[#0e1015] flex-1 overflow-y-auto px-4 py-5 max-w-2xl w-full mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="move-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Cancel
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] flex items-center gap-1">
            <ArrowRightLeft className="w-3 h-3" /> Move photo
          </div>
          <span className="w-12" />
        </div>
        <div className="text-[12px] text-[#647087] mb-3">
          Detaches this photo from every current parent and attaches it to ONE new parent.
        </div>

        <div className="flex gap-1 mb-3 flex-wrap">
          {STAFF_ENTITY_KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => { setKind(k.kind); setPicked(null); setResults([]); setQuery(""); }}
              className={`text-[11px] px-2 py-1 rounded border ${
                kind === k.kind ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"
              }`}
              data-testid={`move-kind-${k.kind}`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
            placeholder={`Search ${kind === "customer" ? "customers" : `${kind}s`}…`}
            className="hv-input text-sm pl-8 pr-7 w-full"
            data-testid="move-search-input"
            autoFocus
          />
          {searching && <Loader2 className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
        </div>

        <div className="space-y-1 mb-4">
          {results.length === 0 && query && !searching && (
            <div className="text-[11px] text-[#647087] py-4 text-center">No matches.</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setPicked(r)}
              className={`w-full text-left text-sm p-3 rounded border ${
                picked?.id === r.id ? "border-[#1FB6C1] bg-[#1FB6C1]/10" : "border-white/10 hover:bg-white/[0.04]"
              }`}
              data-testid={`move-result-${r.id}`}
            >
              <div className="font-medium text-white">{labelFor(r)}</div>
              {r.customer_name && r.customer_name !== labelFor(r) && (
                <div className="text-[10px] text-[#647087]">{r.customer_name}</div>
              )}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2 border-t border-white/5 pt-3 sticky bottom-0 bg-[#0e1015]">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="move-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!picked || busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="move-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />}
            {picked ? `Move to ${labelFor(picked)}` : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignDrawer({ uploadIds, onClose, onDone }) {
  const [kind, setKind] = useState("customer");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let endpoint = "/customers";
        let params = { q: query, limit: 12 };
        if (kind === "jobcard") { endpoint = "/jobcards"; params = { search: query, limit: 12 }; }
        else if (kind === "ticket") { endpoint = "/tickets"; params = { q: query, limit: 12 }; }
        else if (kind === "site") { endpoint = "/sites"; params = { q: query, limit: 12 }; }
        const { data } = await api.get(endpoint, { params, signal: ctl.signal });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        setResults(arr.slice(0, 12));
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, kind]);

  const labelFor = (item) => {
    if (kind === "customer") return item.name || item.trading_name || item.id;
    if (kind === "jobcard") return `${item.short_id || item.ref || item.id?.slice(0, 8)} — ${item.title || ""}`.trim();
    if (kind === "ticket") return `${item.short_id || item.ref || item.id?.slice(0, 8)} — ${item.subject || item.title || ""}`.trim();
    if (kind === "site") return item.label || item.name || item.id;
    return item.id;
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gallery/assign-bulk", {
        upload_ids: uploadIds, kind, id: selected.id,
      });
      onDone(data.assigned || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur flex flex-col" onClick={onClose}>
      <div
        className="bg-[#0e1015] flex-1 overflow-y-auto px-4 py-5 max-w-2xl w-full mx-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="gallery-assign-drawer"
      >
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="assign-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Cancel
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] flex items-center gap-1">
            <Tag className="w-3 h-3" /> Assign {uploadIds.length} item{uploadIds.length === 1 ? "" : "s"}
          </div>
          <span className="w-12" />
        </div>

        <div className="flex gap-1 mb-3 flex-wrap">
          {STAFF_ENTITY_KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              onClick={() => { setKind(k.kind); setSelected(null); setResults([]); setQuery(""); }}
              className={`text-[11px] px-2 py-1 rounded border ${
                kind === k.kind ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]" : "border-white/15 text-[#a0aabf]"
              }`}
              data-testid={`assign-kind-${k.kind}`}
            >
              {k.label}
            </button>
          ))}
        </div>

        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            placeholder={`Search ${kind === "customer" ? "customers" : `${kind}s`}…`}
            className="hv-input text-sm pl-8 pr-7 w-full"
            data-testid="assign-search-input"
            autoFocus
          />
          {searching && <Loader2 className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
        </div>

        <div className="space-y-1 mb-4">
          {results.length === 0 && query && !searching && (
            <div className="text-[11px] text-[#647087] py-4 text-center">No matches.</div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelected(r)}
              className={`w-full text-left text-sm p-3 rounded border ${
                selected?.id === r.id ? "border-[#1FB6C1] bg-[#1FB6C1]/10" : "border-white/10 hover:bg-white/[0.04]"
              }`}
              data-testid={`assign-result-${r.id}`}
            >
              <div className="font-medium text-white">{labelFor(r)}</div>
              {r.customer_name && r.customer_name !== labelFor(r) && (
                <div className="text-[10px] text-[#647087]">{r.customer_name}</div>
              )}
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-2 border-t border-white/5 pt-3 sticky bottom-0 bg-[#0e1015]">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="assign-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!selected || busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="assign-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
            Assign{selected ? ` to ${labelFor(selected)}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
